import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import {
  getAgentStatusLabel,
  getAgentStatusColor
} from '../../constants/agentStatus';
import { useApi } from '../../contexts/ApiContext';

const CALL_STATUSES = ['BUSY', 'INCALL', 'ACW', 'PRECALL'];
const PANEL_WIDTH = 350;

const AgentQuickPanel = ({ open, onClose, agent, campaignLookup }) => {
  const api = useApi();
  const [activeCall, setActiveCall] = useState(null);
  const [callMetadata, setCallMetadata] = useState(null);
  const [loadingCall, setLoadingCall] = useState(false);

  // Fetch active call info when panel opens for an agent in-call
  useEffect(() => {
    if (!open || !agent) {
      setActiveCall(null);
      setCallMetadata(null);
      return;
    }

    if (!CALL_STATUSES.includes(agent.status)) {
      setActiveCall(null);
      setCallMetadata(null);
      return;
    }

    let cancelled = false;
    setLoadingCall(true);

    api.softphone.getActiveCall(agent.agent)
      .then(data => {
        if (cancelled) return;
        setActiveCall(data);
        if (data?.actionId) {
          return api.softphone.getCallMetadata(data.actionId);
        }
        return null;
      })
      .then(meta => {
        if (cancelled) return;
        if (meta?.data) setCallMetadata(meta.data);
      })
      .catch(() => {
        if (!cancelled) {
          setActiveCall(null);
          setCallMetadata(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCall(false);
      });

    return () => { cancelled = true; };
  }, [open, agent?.agent, agent?.status, api]);

  if (!agent) return null;

  const statusLabel = getAgentStatusLabel(agent.status) || agent.status;
  const statusColor = getAgentStatusColor(agent.status);
  const isInCall = CALL_STATUSES.includes(agent.status);

  // callMetadata.skillData = { datastructureFields: ['name1','name2',...], skillData: { name1: val, ... } }
  const renderSkillDataFields = () => {
    const sd = callMetadata?.skillData;
    if (!sd) return null;
    const fieldNames = sd.datastructureFields || [];
    const values = sd.skillData || {};
    if (fieldNames.length === 0 && Object.keys(values).length === 0) return null;

    // If we have fieldNames, use them for ordering; otherwise fall back to object keys
    const keys = fieldNames.length > 0 ? fieldNames : Object.keys(values).filter(k => k !== 'id');

    return keys
      .map(fieldName => {
        const val = values[fieldName];
        if (val === null || val === undefined || val === '') return null;
        return (
          <Box key={fieldName} sx={{ display: 'flex', gap: 1, py: 0.3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
              {fieldName}:
            </Typography>
            <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
              {String(val)}
            </Typography>
          </Box>
        );
      })
      .filter(Boolean);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: PANEL_WIDTH, p: 2 }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" noWrap sx={{ maxWidth: PANEL_WIDTH - 80 }}>
          {agent.name || 'Sin nombre'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Basic info */}
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">ID:</Typography>
          <Typography variant="body2" fontFamily="monospace">{agent.agent}</Typography>
        </Box>
        {agent.extension && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Extensión:</Typography>
            <Typography variant="body2" fontFamily="monospace">{agent.extension}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Estado:</Typography>
          <Chip
            size="small"
            label={statusLabel}
            sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 600 }}
          />
          {isInCall && agent.callDirection && (
            agent.callDirection === 'inbound'
              ? <CallReceivedIcon fontSize="small" color="info" titleAccess="Entrante" />
              : <CallMadeIcon fontSize="small" color="success" titleAccess="Saliente" />
          )}
        </Box>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Skills */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Skills</Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 2 }}>
        {(agent.skills || []).map(skill => (
          <Chip
            key={skill.id}
            size="small"
            label={`${skill.name} (${skill.def})`}
            sx={{ mb: 0.5 }}
          />
        ))}
        {(agent.skills || []).length === 0 && (
          <Typography variant="caption" color="text.secondary">Sin skills activos</Typography>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Campaigns */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Campañas</Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 2 }}>
        {(agent.campaigns || []).map(id => (
          <Chip
            key={id}
            size="small"
            variant="outlined"
            label={campaignLookup?.get(String(id)) || `Campaña ${id}`}
            sx={{ mb: 0.5 }}
          />
        ))}
        {(agent.campaigns || []).length === 0 && (
          <Typography variant="caption" color="text.secondary">Sin campañas</Typography>
        )}
      </Stack>

      {/* Active call section */}
      {isInCall && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon fontSize="small" /> Llamada activa
          </Typography>

          {loadingCall ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeCall?.active ? (
            <Stack spacing={0.5}>
              {activeCall.skill && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Skill:</Typography>
                  <Typography variant="caption">{activeCall.skill}</Typography>
                </Box>
              )}
              {activeCall.phone_number && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Teléfono:</Typography>
                  <Typography variant="caption" fontFamily="monospace">{activeCall.phone_number}</Typography>
                </Box>
              )}
              {activeCall.actionId && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Action ID:</Typography>
                  <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    {activeCall.actionId}
                  </Typography>
                </Box>
              )}
              {agent.callDirection && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Dirección:</Typography>
                  {agent.callDirection === 'inbound'
                    ? <><CallReceivedIcon sx={{ fontSize: 14, color: 'info.main' }} /><Typography variant="caption">Entrante</Typography></>
                    : <><CallMadeIcon sx={{ fontSize: 14, color: 'success.main' }} /><Typography variant="caption">Saliente</Typography></>
                  }
                </Box>
              )}

              {/* Client data from skillData */}
              {callMetadata?.skillData?.skillData && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                    Datos del cliente
                  </Typography>
                  {renderSkillDataFields()}
                </Box>
              )}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Sin datos de llamada disponibles
            </Typography>
          )}
        </>
      )}
    </Drawer>
  );
};

export default AgentQuickPanel;
